import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaExclamationTriangle } from 'react-icons/fa';
import api from '../api';
import { useAuth } from '../AuthContext';
import { formatDateOnly, normalizeDateInput } from '../utils/dateOnly';
import { normalizeStoredUrl, resolveAssetUrl } from '../utils/assets';

const formatCpf = (cpf) => {
  if (!cpf) return '—';
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return cpf;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

export default function Profile({
  forcePasswordChange = false,
  closeTab,
  activateTab,
}) {
  const { user, updateUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [birthDate, setBirthDate] = useState(normalizeDateInput(user?.birthDate));
  const [sectorId, setSectorId] = useState(user?.sector?.id ? String(user.sector.id) : '');
  const [cpf, setCpf] = useState(user?.cpf || '');
  const [notifyByEmail, setNotifyByEmail] = useState(user?.emailNotificationsEnabled !== false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [photoPreview, setPhotoPreview] = useState(() => normalizeStoredUrl(user?.photoUrl || ''));
  const [photoFile, setPhotoFile] = useState(null);
  const [photoRemoved, setPhotoRemoved] = useState(false);
  const [loading, setLoading] = useState(false);
  const photoInputRef = useRef(null);

  useEffect(() => {
    setName(user?.name || '');
    setEmail(user?.email || '');
    setBirthDate(normalizeDateInput(user?.birthDate));
    setSectorId(user?.sector?.id ? String(user.sector.id) : '');
    setCpf(user?.cpf || '');
    setNotifyByEmail(user?.emailNotificationsEnabled !== false);
    setConfirmPassword('');
    setPhotoPreview(normalizeStoredUrl(user?.photoUrl || ''));
    setPhotoFile(null);
    setPhotoRemoved(false);
  }, [user]);

  const mustChangePassword = Boolean(user?.mustChangePassword);
  const requirePasswordChange = forcePasswordChange || mustChangePassword;

  const hasChanges = useMemo(() => {
    const originalName = user?.name || '';
    const originalEmail = user?.email || '';
    const originalBirthDate = user?.birthDate || '';
    const originalCpf = user?.cpf || '';
    const originalNotify = user?.emailNotificationsEnabled !== false;
    const originalPhoto = normalizeStoredUrl(user?.photoUrl || '');
    const trimmedName = name.trim();
    const previewPhoto = normalizeStoredUrl(photoPreview || '');
    const hasPhotoChanges = photoRemoved || Boolean(photoFile) || previewPhoto !== originalPhoto;

    return (
      Boolean(password) ||
      trimmedName !== originalName ||
      email.trim() !== originalEmail ||
      birthDate !== originalBirthDate ||
      cpf !== originalCpf ||
      notifyByEmail !== originalNotify ||
      hasPhotoChanges
    );
  }, [
    birthDate,
    cpf,
    email,
    name,
    notifyByEmail,
    password,
    photoFile,
    photoPreview,
    photoRemoved,
    user,
  ]);

  const profileInfo = [
    { label: 'Nome', value: user?.name || '—', colSpan: 'md:col-span-2' },
    { label: 'E-mail', value: email || '—', colSpan: 'md:col-span-2' },
    { label: 'Grupo', value: user?.group?.name || '—' },
    { label: 'Data de nascimento', value: formatDateOnly(birthDate) },
    { label: 'Setor', value: user?.sector?.name || '—' },
    { label: 'CPF', value: formatCpf(cpf) },
    {
      label: 'Notificações por e-mail',
      value: notifyByEmail ? 'Ativadas' : 'Desativadas',
      colSpan: 'md:col-span-2',
    },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasChanges) return;

    const trimmedPassword = password.trim();
    const trimmedConfirmPassword = confirmPassword.trim();
    const trimmedName = name.trim();

    if (!trimmedName) {
      alert('Informe um nome para continuar.');
      return;
    }

    if (requirePasswordChange && !trimmedPassword) {
      alert('Informe uma nova senha para continuar.');
      return;
    }

    if (trimmedPassword && trimmedPassword !== trimmedConfirmPassword) {
      alert('As senhas digitadas não coincidem.');
      return;
    }

    setLoading(true);
    const passwordProvided = Boolean(trimmedPassword);
    const wasRequiredChange = requirePasswordChange;

    try {
      let nextPhotoUrl = user?.photoUrl ?? null;
      if (photoRemoved) {
        nextPhotoUrl = null;
      }
      if (photoFile) {
        const formData = new FormData();
        formData.append('file', photoFile);
        const uploadResponse = await api.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const uploadData = uploadResponse?.data ?? {};
        nextPhotoUrl =
          uploadData.url ||
          uploadData.downloadUrl ||
          uploadData.downloadPath ||
          (typeof uploadData.relativePath === 'string' ? `/uploads/${uploadData.relativePath}` : null);
      }

      const photoHasChanged = photoRemoved || Boolean(photoFile) || nextPhotoUrl !== user?.photoUrl;
      const res = await api.put('/users/me', {
        password: trimmedPassword,
        birthDate: birthDate || null,
        sectorId: sectorId ? Number(sectorId) : undefined,
        cpf: cpf || null,
        email: email.trim(),
        emailNotificationsEnabled: notifyByEmail,
        name: trimmedName,
        ...(photoHasChanged ? { photoUrl: nextPhotoUrl } : {}),
      });
      updateUser(res.data);
      setPassword('');
      setConfirmPassword('');
      setName(res.data.name || '');
      setEmail(res.data.email || '');
      setBirthDate(normalizeDateInput(res.data.birthDate));
      setSectorId(res.data.sector?.id ? String(res.data.sector.id) : '');
      setCpf(res.data.cpf || '');
      setNotifyByEmail(res.data.emailNotificationsEnabled !== false);
      setPhotoPreview(normalizeStoredUrl(res.data.photoUrl || ''));
      setPhotoFile(null);
      setPhotoRemoved(false);
      alert('Perfil atualizado');

      if (wasRequiredChange && passwordProvided) {
        activateTab?.('home');
        closeTab?.({ activateTabId: 'home' });
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Erro ao atualizar';
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoChange = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      alert('Selecione um arquivo de imagem.');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(typeof reader.result === 'string' ? reader.result : '');
    };
    reader.readAsDataURL(file);
    setPhotoFile(file);
    setPhotoRemoved(false);
    if (event.target) {
      event.target.value = '';
    }
  };

  const handlePhotoRemove = () => {
    setPhotoPreview('');
    setPhotoFile(null);
    setPhotoRemoved(true);
  };

  const resolvedPhotoPreviewUrl = useMemo(() => {
    if (!photoPreview) {
      return '';
    }
    if (/^(data:|blob:)/i.test(photoPreview) || photoFile) {
      return photoPreview;
    }
    return resolveAssetUrl(photoPreview);
  }, [photoFile, photoPreview]);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {requirePasswordChange && (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-700 shadow">
          <span className="mt-1 inline-flex h-9 w-9 items-center justify-center rounded-full border border-amber-200 text-amber-600">
            <FaExclamationTriangle />
          </span>
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide">Troca de senha obrigatória</h2>
            <p className="text-sm text-amber-700">
              Por segurança, defina uma nova senha antes de continuar utilizando a plataforma.
            </p>
          </div>
        </div>
      )}
      <div>
        <h1 className="text-2xl font-semibold">Meu Perfil</h1>
        <p className="text-gray-600">Consulte seus dados e atualize sua senha.</p>
      </div>

      <section className="bg-white shadow rounded-lg p-4">
        <h2 className="text-lg font-medium mb-3">Informações pessoais</h2>
        <div className="space-y-1 mb-4">
          <p className="font-medium">{user?.name || 'Usuário'}</p>
          <p className="text-sm text-gray-500 break-words">{email || '—'}</p>
          <p className="text-sm text-gray-500">{user?.group?.name}</p>
        </div>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {profileInfo.map((item) => (
            <div key={item.label} className={`space-y-1 ${item.colSpan || ''}`}>
              <dt className="text-sm text-gray-500">{item.label}</dt>
              <dd className="text-base font-medium text-gray-900">{item.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="bg-white shadow rounded-lg p-4">
        <h2 className="text-lg font-medium mb-3">Atualizar acesso</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="block text-sm font-medium text-gray-700">
            <span className="mb-1 block">Nome completo</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="border rounded px-2 py-1 w-full"
              placeholder="Como deseja ser identificado"
            />
          </label>
          <div>
            <span className="mb-1 block text-sm font-medium text-gray-700">Foto de perfil</span>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              {resolvedPhotoPreviewUrl ? (
                <img
                  src={resolvedPhotoPreviewUrl}
                  alt="Pré-visualização da foto do perfil"
                  className="h-20 w-20 rounded-full object-cover border"
                />
              ) : (
                <div className="flex h-20 w-20 items-center justify-center rounded-full border border-dashed border-gray-300 text-xs text-gray-500">
                  Sem foto
                </div>
              )}
              <div className="flex flex-col gap-2 text-sm font-medium">
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  className="rounded border border-blue-200 px-3 py-1 text-blue-600 hover:bg-blue-50"
                >
                  Escolher foto
                </button>
                {photoPreview && (
                  <button
                    type="button"
                    onClick={handlePhotoRemove}
                    className="rounded border border-gray-200 px-3 py-1 text-gray-600 hover:bg-gray-50"
                  >
                    Remover foto
                  </button>
                )}
                <p className="text-xs font-normal text-gray-500">
                  Utilize imagens JPG, PNG ou GIF. Esta foto será exibida em recursos como o Nash Chat.
                </p>
              </div>
            </div>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              className="hidden"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm font-medium text-gray-700 sm:col-span-2">
              <span className="mb-1 block">E-mail</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border rounded px-2 py-1 w-full"
                placeholder="seuemail@empresa.com"
              />
            </label>
            <label className="block text-sm font-medium text-gray-700">
              <span className="mb-1 block">Data de nascimento</span>
              <input
                type="date"
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
                className="border rounded px-2 py-1 w-full"
              />
            </label>
            <div className="block text-sm font-medium text-gray-700">
              <span className="mb-1 block">Setor</span>
              <div className="border rounded px-3 py-2 w-full bg-gray-50 text-gray-600">
                {user?.sector?.name || '—'}
              </div>
              <p className="mt-1 text-xs text-gray-500">Definido pelo administrador.</p>
            </div>
          </div>
          <label className="block text-sm font-medium text-gray-700">
            <span className="mb-1 block">CPF</span>
            <input
              type="text"
              value={cpf}
              onChange={(e) => setCpf(e.target.value.replace(/\D/g, ''))}
              placeholder="Somente números"
              maxLength={11}
              className="border rounded px-2 py-1 w-full"
            />
          </label>
          <div>
            <label className="block mb-1 font-medium">
              Nova Senha
              {requirePasswordChange && <span className="ml-1 text-xs font-semibold text-amber-600">(obrigatória)</span>}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="border rounded px-2 py-1 w-full"
              required={requirePasswordChange}
            />
          </div>
          <div>
            <label className="block mb-1 font-medium">Confirmar nova senha</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="border rounded px-2 py-1 w-full"
              required={Boolean(password)}
            />
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={notifyByEmail}
                onChange={(e) => setNotifyByEmail(e.target.checked)}
                className="mt-1"
              />
              <span>
                Receber notificações por e-mail quando novas tarefas forem atribuídas a você. O envio utiliza o e-mail do
                seu cadastro.
              </span>
            </label>
          </div>
          <button
            type="submit"
            disabled={loading || !hasChanges}
            className="bg-blue-600 text-white px-3 py-1 rounded disabled:opacity-50"
          >
            {loading ? 'Salvando...' : 'Salvar alterações'}
          </button>
        </form>
      </section>
    </div>
  );
}
